import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Slider } from "../ui/slider";
import { DollarSign, ChevronDown, ChevronUp } from "lucide-react";
import { InvestorPaymentTimeline } from "./InvestorPaymentTimeline";

export function FinancingOptionsSection({
  investorType,
  setInvestorType,
  rentalPeriod,
  upfrontPaymentPct,
  setUpfrontPaymentPct,
  paymentParts,
  setPaymentParts,
  results,
  readOnly = false,
}) {
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);

  return (
    <Card className="shadow-md">
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
          Financing Options
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0 space-y-4 sm:space-y-6">
        <div className="space-y-3">
          <Label htmlFor="investor-type" className="text-xs sm:text-sm">
            Investor Return - Annually
          </Label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <Slider
              id="investor-type-slider"
              min={8}
              max={30}
              step={1}
              value={[investorType]}
              onValueChange={(value) => setInvestorType(value[0])}
              className="flex-1"
              disabled={readOnly}
            />
            <span className="text-sm sm:text-base font-semibold text-gray-700 sm:min-w-[140px] text-left sm:text-right">
              {investorType}% Interest p.a.
            </span>
          </div>
        </div>

        <div className="space-y-3">
          <Label htmlFor="upfront-payment-pct" className="text-xs sm:text-sm">
            Upfront Payment Required (% of Construction Loan)
          </Label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <Slider
              id="upfront-payment-pct"
              min={10}
              max={100}
              step={5}
              value={[upfrontPaymentPct]}
              onValueChange={(value) => setUpfrontPaymentPct(value[0])}
              className="flex-1"
              disabled={readOnly}
            />
            <span className="text-sm sm:text-base font-semibold text-gray-700 sm:min-w-[140px] text-left sm:text-right">
              {upfrontPaymentPct}% Upfront
            </span>
          </div>
        </div>

        <div className="space-y-3">
          <Label htmlFor="payment-parts" className="text-xs sm:text-sm">
            Remaining Payment Parts (Equal Installments)
          </Label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <Slider
              id="payment-parts"
              min={1}
              max={6}
              step={1}
              value={[paymentParts]}
              onValueChange={(value) => setPaymentParts(value[0])}
              className="flex-1"
              disabled={readOnly}
            />
            <span className="text-sm sm:text-base font-semibold text-gray-700 sm:min-w-[140px] text-left sm:text-right">
              {paymentParts} Parts
            </span>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {((100 - upfrontPaymentPct) / paymentParts).toFixed(1)}% per
            installment over {rentalPeriod} months
          </p>
        </div>

        {/* Investor Payment Timeline Chart - Collapsed by default */}
        <div>
          <button
            type="button"
            onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
            className="w-full flex items-center justify-between p-2 sm:p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer"
          >
            <span className="text-sm sm:text-base font-medium text-gray-700">
              Payment Timeline
            </span>
            {isTimelineExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </button>
          {isTimelineExpanded && (
            <div className="mt-2 animate-in slide-in-from-top-2 duration-200">
              <InvestorPaymentTimeline
                results={results}
                rentalPeriod={rentalPeriod}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
